import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfCheckboxesField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfMultipleLineInputField,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
  FrVrfSingleLinkDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation, createPath } from "react-router-dom";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {
  CpntAttr,
  EdhPrtclInfo,
  GetEdhPrtclAttribute,
  useGetComponent,
  useUpdateComponentAttributes,
} from "../../services/component";
import { UpdateComponentAttributesRequest } from "../../services/component/update-component-attributes/UpdateComponentAttributesRequest";
import {
  useGetEdhNotificationTypeList,
  useGetEdhProtocolCombinationList,
  useGetEdhProtocolList,
  useGetGeneralCodes,
} from "../../services/data-list";
import {
  CreateDeploymentRequestComponentNameRequest,
  useCreateDeploymentRequestComponentName,
} from "../../services/deployment-request";
import {
  CreateDynamicEdhProtocol,
  createDynamicEdhProtocol,
} from "../../util/edh-component-logic";
import {
  DeploymentRequestScreenProcessingDivisionType,
  EnvTypeCd,
  InDefCommonFlagType,
  IoType,
  TcktStsTypeCpntDsgn,
  TcktStsTypeCpntPrdDplymnt,
  TcktStsTypeCpntPrjPrgrs,
  TcktStsTypeCpntStgDplymnt,
} from "../../util/general-code/enums/general-codes";
import {
  ErrorResponse,
  INIT_ERROR_RESULT_DATA,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import {
  ComponentEditDialogContent,
  useComponentEditDialogState,
} from "../dialog/component-edit-dialog";
import {
  RootComponentEditDialogContent,
  useRootComponentEditDialogState,
} from "../dialog/root-component-edit-dialog";
import { PAGE_MODE as EDH_PROTOCOL_ATTRIBUTE_PAGE_MODE } from "../edh-protocol-attribute/EdhProtocolAttributePageConstant";
import {
  EdhProtocolSpreadsheetState,
  EditCell,
  PicAddressSpreadsheetState,
  SpreadsheetEdhPrtcl,
} from "./";
import {
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  isChecked,
  requestCdTypes,
} from "./ComponentDetailPageConstant";
import { usePageState } from "./usePageState";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const ComponentDetailPageContent = () => {
  const navigate = useNavigate();
  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const { showConfirmationDialog, showToast, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();

  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { searchResult, dataList } = pageState;

  const params = {
    dipByRgn: queryParameter.get("dipByRgn") ?? "",
    rootCpntId: queryParameter.get("rootCpntId") ?? "",
    cpntId: queryParameter.get("cpntId") ?? "",
    godFlg: queryParameter.get("godFlg") ?? InDefCommonFlagType.NO,
  };

  const isGodMode: boolean = params.godFlg === InDefCommonFlagType.YES;

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getEdhProtocolListResponse, sendGetEdhProtocolListRequest] =
    useGetEdhProtocolList();
  const [
    getInboundEdhNotificationTypeListResponse,
    sendGetInboundEdhNotificationTypeListRequest,
  ] = useGetEdhNotificationTypeList();
  const [
    getOutboundEdhNotificationTypeListResponse,
    sendGetOutboundEdhNotificationTypeListRequest,
  ] = useGetEdhNotificationTypeList();
  const [getComponentResponse, sendGetComponentRequest] = useGetComponent();
  const [
    updateComponentAttributesResponse,
    sendUpdateComponentAttributesRequest,
  ] = useUpdateComponentAttributes();
  const [
    createDeploymentRequestComponentNameResponse,
    setCreateDeploymentRequestComponentNameRequest,
  ] = useCreateDeploymentRequestComponentName();
  const [rootComponentEditDialogState, setRootComponentEditDialogState] =
    useRootComponentEditDialogState();
  const [componentEditDialogState, setComponentEditDialogState] =
    useComponentEditDialogState();
  const [getEdhProtocolCombinationResponse, sendEdhProtocolCombinationRequest] =
    useGetEdhProtocolCombinationList();

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState({
      ...appState,
      title: "Component Detail",
      isGodMode,
    });

    sendGetGeneralCodesRequest({ cdTypes: requestCdTypes });
    sendGetEdhProtocolListRequest({ godOnlyFlag: params.godFlg });
    sendGetInboundEdhNotificationTypeListRequest({
      inOutTypeCd: IoType.INBOUND,
      dipByRgn: params.dipByRgn,
    });
    sendGetOutboundEdhNotificationTypeListRequest({
      inOutTypeCd: IoType.OUTBOUND,
      dipByRgn: params.dipByRgn,
    });
    sendGetComponentRequest({
      rootComponentId: params.rootCpntId,
      componentId: params.cpntId,
      dipByRgn: params.dipByRgn,
      godFlg: params.godFlg,
    });
    sendEdhProtocolCombinationRequest({});
  };

  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const cpntOwnType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CPNT_OWN_TYPE,
      )?.cdList ?? [];

    const cpntAlctnType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CPNT_ALCTN_TYPE,
      )?.cdList ?? [];

    const clodSrvc =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CLOD_SRVC,
      )?.cdList ?? [];

    const clodSrvcRgn =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CLOD_SRVC_RGN,
      )?.cdList ?? [];

    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE,
      )?.cdList ?? [];

    const startMethod =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.INTERFACE_START_METHOD_TYPE,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        cpntOwnType,
        cpntAlctnType,
        clodSrvc,
        clodSrvcRgn,
        inDefCommonFlagType,
        startMethod,
      },
    }));
  };

  const onGetInboundEdhNotificationTypeListResponse = () => {
    if (!getInboundEdhNotificationTypeListResponse) return;
    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inboundNotificator:
          getInboundEdhNotificationTypeListResponse.edhNotificationTypeList.map(
            (e) => ({
              label: e.edhNtfctnTypeName,
              value: e.edhNtfctnTypeCd,
            }),
          ),
      },
    }));
  };

  const onGetOutboundEdhNotificationTypeListResponse = () => {
    if (!getOutboundEdhNotificationTypeListResponse) return;
    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        outboundNotificator:
          getOutboundEdhNotificationTypeListResponse.edhNotificationTypeList.map(
            (e) => ({
              label: e.edhNtfctnTypeName,
              value: e.edhNtfctnTypeCd,
            }),
          ),
      },
    }));
  };

  const onGetComponentResponse = () => {
    if (!getComponentResponse) return;
    setPageState((prevState) => ({
      ...prevState,
      searchResult: getComponentResponse,
    }));
  };

  const onGetEdhProtocolFlagUsageListResponse = () => {
    if (!getEdhProtocolCombinationResponse || !getEdhProtocolListResponse)
      return;
    const edhProtcolList = getEdhProtocolListResponse.edhProtocolList
      .filter((e) => isGodMode || e.godOnlyFlag === InDefCommonFlagType.NO)
      .map((e) => ({
        label: e.edhPrtclDsplyName,
        value: e.inOutTypeCd + "/" + e.edhPrtclTypeCd,
      }));
    setPageState((prevState) => ({
      ...prevState,
      edhProtocolCombination:
        getEdhProtocolCombinationResponse.edhPrtclCmbntnDfnList,
      dataList: {
        ...prevState.dataList,
        edhProtcolList,
      },
    }));
  };

  const onUpdateComponentAttributes = () => {
    if (!updateComponentAttributesResponse) return;
    if (Array.isArray(updateComponentAttributesResponse)) {
      if (!edhProtocolSpreadsheetState) {
        return;
      }

      // copy and mapping error response to spreadsheet state.
      const currentEdhPrtcl: SpreadsheetEdhPrtcl[] =
        edhProtocolSpreadsheetState.edhPrtcl;
      const emptyColumnList = [{ label: "", value: "" }];
      const newSpreadsheetRecordData: SpreadsheetEdhPrtcl[] =
        mappingErrorResponseToSpreadsheet(
          updateComponentAttributesResponse,
          currentEdhPrtcl,
          emptyColumnList,
        );

      // copy and set error flag to to spreadsheet state.
      let copyEditCell: EditCell = JSON.parse(
        JSON.stringify(edhProtocolSpreadsheetState.editCell),
      );
      updateComponentAttributesResponse.forEach((error: ErrorResponse) => {
        error.errorColumnList.forEach((errorColumn: string) => {
          const cell = copyEditCell[errorColumn];
          cell.isError = true;
        });
      });

      setEdhProtocolSpreadsheetState({
        ...edhProtocolSpreadsheetState,
        edhPrtcl: newSpreadsheetRecordData,
        editCell: copyEditCell,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetComponentRequest({
        rootComponentId: params.rootCpntId,
        componentId: params.cpntId,
        dipByRgn: params.dipByRgn,
        godFlg: params.godFlg,
      });
    }
  };

  const onCreateDeploymentRequestComponentNameResponse = () => {
    if (createDeploymentRequestComponentNameResponse) {
      if (
        createDeploymentRequestComponentNameResponse.warningMessage &&
        createDeploymentRequestComponentNameResponse.warningMessage.length > 0
      ) {
        showConfirmationDialog({
          type: "warning",
          title: "",
          message:
            createDeploymentRequestComponentNameResponse.warningMessage.join(
              "\n",
            ),
          onOkButtonClicked: () => {
            const deploymentRequestCmpName =
              {} as CreateDeploymentRequestComponentNameRequest;
            deploymentRequestCmpName.dipByRgn = params.dipByRgn;
            deploymentRequestCmpName.cpntId = params.cpntId;
            deploymentRequestCmpName.forceStgDplyFlg = true;
            setCreateDeploymentRequestComponentNameRequest(
              deploymentRequestCmpName,
            );
          },
        });
        return;
      }
      const searchParam = {
        screenMode: DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE,
        componentId: params.cpntId,
        dipByRegion: params.dipByRgn,
        environment: EnvTypeCd.STG,
      };
      transition(
        `/component-deployment-request-list?${stringify(searchParam)}`,
      );
    }
  };

  const onUpdateDynamicCheckboxes = () => {
    const createDynamicEdhProtocolProps: CreateDynamicEdhProtocol = {
      edhProtocolCombinationList: pageState.edhProtocolCombination,
      edhProtocolList: dataList.edhProtcolList,
      edhProtocolCheckList: searchResult.edhPrtclInfoList
        .filter((e) => e.godFlg === params.godFlg)
        .filter((e) => e.cpntEdhPrtclMngDelFlg === InDefCommonFlagType.NO)
        .map((e) => e.inOutTypeCd + "/" + e.edhPrtclTypeCd),
    };

    const { dynamicEdhProtocolList } = createDynamicEdhProtocol(
      createDynamicEdhProtocolProps,
    );

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        dynamicEdhProtocolList,
      },
    }));
  };

  const getEditCells = (edhPrtclInfo: SpreadsheetEdhPrtcl[]) => {
    let set = new Set();
    let res: EditCell = {};
    const addEditcells = (key: string, initValue: string) => {
      if (!set.has(key)) {
        set.add(key);
        res[key] = {
          isDirty: false,
          initialValue: initValue,
          currentValue: "",
          isError: false,
        };
      }
    };
    edhPrtclInfo.forEach((edhPrtcl: SpreadsheetEdhPrtcl) => {
      edhPrtcl.edhPrtclInfo.edhPrtclAttributeList.forEach(
        (attribute: GetEdhPrtclAttribute) => {
          addEditcells(
            "stgDesignValue" +
              "-" +
              edhPrtcl.rowIdx +
              "-" +
              attribute.edhPrtclAttributenum,
            attribute.stgDesignValue,
          );
          addEditcells(
            "prdDesignValue" +
              "-" +
              edhPrtcl.rowIdx +
              "-" +
              attribute.edhPrtclAttributenum,
            attribute.prdDesignValue,
          );
        },
      );
    });
    return res;
  };

  useEffect(onInitialDisplayed, [params.godFlg]);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetInboundEdhNotificationTypeListResponse, [
    getInboundEdhNotificationTypeListResponse,
  ]);
  useEffect(onGetOutboundEdhNotificationTypeListResponse, [
    getOutboundEdhNotificationTypeListResponse,
  ]);
  useEffect(onGetComponentResponse, [getComponentResponse]);
  useEffect(onGetEdhProtocolFlagUsageListResponse, [
    getEdhProtocolCombinationResponse,
    getEdhProtocolListResponse,
  ]);
  useEffect(onUpdateComponentAttributes, [updateComponentAttributesResponse]);
  useEffect(onCreateDeploymentRequestComponentNameResponse, [
    createDeploymentRequestComponentNameResponse,
  ]);
  useEffect(onUpdateDynamicCheckboxes, [
    pageState.edhProtocolCombination,
    searchResult.edhPrtclList,
    searchResult.gpgEncryptnFlg,
    searchResult.hubFlg,
    searchResult.ediUsgFlg,
    pageState.dataList.edhProtcolList,
  ]);

  /**
   * GOD
   */
  const GodContent: React.FC = () => {
    const onClickGodConfirmDialogHandle = () => {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00046),
        type: "warning",
        onOkButtonClicked: () => {
          resetUserError();
          const newParams = {
            ...params,
            godFlg: InDefCommonFlagType.YES,
          };
          transition(`/component-detail?${stringify(newParams)}`);
        },
      });
    };
    return (
      <FrVrfButtonPanel>
        <FrVrfButton
          content="IE Maintenance"
          size="medium"
          theme="caution"
          onClicked={onClickGodConfirmDialogHandle}
          authActId="component_detail.button.god"
        />
      </FrVrfButtonPanel>
    );
  };
  /**
   * Root Component
   */
  const [
    _picAddressSpreadsheetState,
    _setPicAddressSpreadsheetState,
    initPicAddressSpreadsheetState,
  ] = useSpreadsheetState<PicAddressSpreadsheetState>();

  const picAddressSpreadsheetInitialData = useMemo<PicAddressSpreadsheetState>(
    () => ({ results: searchResult.rootCpntIchrList }),
    [searchResult.rootCpntIchrList],
  );

  const onClickRootComponentEditButton = () => {
    const openRootComponentEditDialog = () => {
      setRootComponentEditDialogState((prevState) => ({
        ...prevState,
        isShow: true,
        rootComponent: pageState.searchResult,
        dataList: { ...dataList },
      }));
    };

    if (isChangedEdhProtocolAttribute) {
      createDiscardDialog(openRootComponentEditDialog);
    } else {
      openRootComponentEditDialog();
    }
  };

  const onClickedPartnerListLink = () => {
    const navigationParameters = {
      dipByRgn: params.dipByRgn,
      cpntId: params.cpntId,
    };

    if (isChangedEdhProtocolAttribute) {
      createDiscardDialog(() =>
        transition(`/partner-list?${stringify(navigationParameters)}`),
      );
    } else {
      transition(`/partner-list?${stringify(navigationParameters)}`);
    }
  };

  const onCloseRootComponentEditDialog = () => {
    if (
      !rootComponentEditDialogState.isShow &&
      rootComponentEditDialogState.isUpdated
    ) {
      onInitialDisplayed();
    }
  };

  useEffect(onCloseRootComponentEditDialog, [
    rootComponentEditDialogState.isShow,
  ]);

  /**
   *  Component
   */
  const onClickDeployComponentNameButton = () => {
    resetUserError();
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: () => {
        const deploymentRequestCmpName: CreateDeploymentRequestComponentNameRequest =
          {
            dipByRgn: params.dipByRgn,
            cpntId: params.cpntId,
            forceStgDplyFlg: false,
          };
        setCreateDeploymentRequestComponentNameRequest(
          deploymentRequestCmpName,
        );
      },
    });
  };

  const onClickComponentEditButton = () => {
    const inboundNotificator = pageState.searchResult.edhNtfctnTypeList.filter(
      (e) => e.inOutTypeCd === IoType.INBOUND,
    );
    const outboundNotificator = pageState.searchResult.edhNtfctnTypeList.filter(
      (e) => e.inOutTypeCd === IoType.OUTBOUND,
    );

    const openComponentEditDialog = () => {
      setComponentEditDialogState((prevState) => ({
        ...prevState,
        isShow: true,
        isUpdated: false,
        component: pageState.searchResult,
        formEditData: {
          edhPrtclList: pageState.searchResult.edhPrtclInfoList
            .filter((e) => e.godFlg === params.godFlg)
            .filter((e) => e.cpntEdhPrtclMngDelFlg === InDefCommonFlagType.NO)
            .map((e) => e.inOutTypeCd + "/" + e.edhPrtclTypeCd),
          ifStrtMthdList: pageState.searchResult.ifStrtMthdList.map(
            (e) => e.ifStrtMthdTypeCd,
          ),
          inboundNotificatorList: inboundNotificator.map(
            (e) => e.edhNtfctnTypeCd,
          ),
          outboundNotificatorList: outboundNotificator.map(
            (e) => e.edhNtfctnTypeCd,
          ),
        },
        edhProtocolCombination: pageState.edhProtocolCombination,
        dataList: pageState.dataList,
      }));
    };

    if (isChangedEdhProtocolAttribute) {
      createDiscardDialog(openComponentEditDialog);
    } else {
      openComponentEditDialog();
    }
  };

  const onCloseComponentEditDialog = () => {
    if (
      !componentEditDialogState.isShow &&
      componentEditDialogState.isUpdated
    ) {
      onInitialDisplayed();
    }
  };

  useEffect(onCloseComponentEditDialog, [componentEditDialogState.isShow]);

  /**
   *  EDH Protocol Attribute
   */
  const [
    edhProtocolSpreadsheetState,
    setEdhProtocolSpreadsheetState,
    initEdhProtocolSpreadsheetState,
  ] = useSpreadsheetState<EdhProtocolSpreadsheetState>();

  const isEditableAttr = (
    targetAttr: GetEdhPrtclAttribute,
    targetIdx: number,
    targetPrtcl: EdhPrtclInfo,
    prtcls: EdhPrtclInfo[],
  ): boolean => {
    // If this protocol has no attribte, the properties of
    //  edhPrtclAttributeList[0] is empty string ""
    if (!targetPrtcl.edhPrtclAttributeList[0].key) {
      return false;
    }

    return !prtcls.some(
      (p, idx) =>
        targetIdx !== idx &&
        p.designStatus === TcktStsTypeCpntDsgn.DESIGN_APPROVED &&
        ((p.stgOnlyFlg === InDefCommonFlagType.YES &&
          p.stgDeployStatus !== TcktStsTypeCpntStgDplymnt.DEPLOYED) ||
          p.prdDeployStatus !== TcktStsTypeCpntPrdDplymnt.DEPLOYED) &&
        p.edhPrtclAttributeList.some(
          (a) => a.edhPrtclAttributenum === targetAttr.edhPrtclAttributenum,
        ),
    );
  };

  const edhProtocolSpreadsheetInitialData =
    useMemo<EdhProtocolSpreadsheetState>(() => {
      const newEdhProtocolInfoList = searchResult.edhPrtclInfoList;
      const spreatsheetEdhPrtclList: SpreadsheetEdhPrtcl[] =
        newEdhProtocolInfoList.map((e, idx) => {
          const spreadsheetEdhPrtcl: SpreadsheetEdhPrtcl = {
            ...INIT_ERROR_RESULT_DATA,
            isChangedPrd: false,
            isChangedStg: false,
            isEditableAttr: !!e.edhPrtclAttributeList[0].key,
            rowIdx: idx,
            edhPrtclInfo: {
              ...e,
              edhPrtclAttributeList: e.edhPrtclAttributeList.map((a) => ({
                ...a,
                isEditableAttr: isEditableAttr(
                  a,
                  idx,
                  e,
                  newEdhProtocolInfoList,
                ),
              })),
            },
          };
          return spreadsheetEdhPrtcl;
        });

      return {
        edhPrtcl: spreatsheetEdhPrtclList,
        godFlg: params.godFlg,
        editCell: getEditCells(spreatsheetEdhPrtclList),
        dataList: pageState.dataList,
      };
    }, [searchResult.edhPrtclInfoList, params.godFlg, pageState.dataList]);

  const changedEdhPrtclList = useMemo(() => {
    if (!edhProtocolSpreadsheetState) return [];
    const edhPrtclList: UpdateComponentAttributesRequest["cpntAttrList"] =
      edhProtocolSpreadsheetState.edhPrtcl.flatMap((edhPrtcl, index) => {
        const newEdhPrtclAttribute =
          edhPrtcl?.edhPrtclInfo.edhPrtclAttributeList?.flatMap(
            (edhPrtclAttribute) => {
              let sendDataList: UpdateComponentAttributesRequest["cpntAttrList"] =
                [];

              const stg =
                "stgDesignValue-" +
                index +
                "-" +
                edhPrtclAttribute.edhPrtclAttributenum;
              const prd =
                "prdDesignValue-" +
                index +
                "-" +
                edhPrtclAttribute.edhPrtclAttributenum;

              const isChangedPrd =
                edhProtocolSpreadsheetState.editCell[prd]?.isDirty;
              const isChangedStg =
                edhProtocolSpreadsheetState.editCell[stg]?.isDirty;

              if (isChangedPrd) {
                const changeData: CpntAttr = {
                  envTypeCd: EnvTypeCd.PRD,
                  cpntPjtId: edhPrtcl.edhPrtclInfo.inProgressCmpProjectId,
                  cpntAttrNum: edhPrtclAttribute.edhPrtclAttributenum,
                  updateDateTime:
                    edhPrtclAttribute.updateDateTimePrdDesignValue,
                  stngVal:
                    edhProtocolSpreadsheetState.editCell[prd].currentValue,
                  index: edhPrtcl.rowIdx,
                };
                sendDataList = [...sendDataList, changeData];
              }
              if (isChangedStg) {
                const changeData: CpntAttr = {
                  envTypeCd: EnvTypeCd.STG,
                  cpntPjtId: edhPrtcl.edhPrtclInfo.inProgressCmpProjectId,
                  cpntAttrNum: edhPrtclAttribute.edhPrtclAttributenum,
                  updateDateTime:
                    edhPrtclAttribute.updateDateTimeStgDesignValue,
                  stngVal:
                    edhProtocolSpreadsheetState.editCell[stg].currentValue,
                  index: edhPrtcl.rowIdx,
                };
                sendDataList = [...sendDataList, changeData];
              }
              return sendDataList;
            },
          );
        return newEdhPrtclAttribute;
      });
    return edhPrtclList;
  }, [edhProtocolSpreadsheetState]);

  const isChangedEdhProtocolAttribute = useMemo(
    () => changedEdhPrtclList.length > 0,
    [changedEdhPrtclList],
  );

  const createDiscardDialog = (onOkButtonClicked: () => void) => {
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked,
    });
  };

  const onClickEdhProtocolSpreadsheet = (event: CustomEvent) => {
    const eventName = event.detail.name;
    const selectedRow = event.detail.value;

    const inProgressCmpProjectNameLogic = () => {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        cpntPjtSts: TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
      };
      if (isChangedEdhProtocolAttribute) {
        createDiscardDialog(() =>
          transition(
            `/component-project-list?${stringify(navigationParameters)}`,
          ),
        );
      } else {
        transition(
          `/component-project-list?${stringify(navigationParameters)}`,
        );
      }
    };

    const waitingCmpProjectNameLogic = () => {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        cpntPjtSts:
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_REQUIREMENT_DESIGN,
      };
      if (isChangedEdhProtocolAttribute) {
        createDiscardDialog(() =>
          transition(
            `/component-project-list?${stringify(navigationParameters)}`,
          ),
        );
      } else {
        transition(
          `/component-project-list?${stringify(navigationParameters)}`,
        );
      }
    };
    const inProgressCmpProjectNameNewTabIconLogic = () => {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        cpntPjtSts: TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
      };
      const href = createPath({
        pathname: `/component-project-list?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    };

    const waitingCmpProjectNameNewTabIconLogic = () => {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        cpntPjtSts:
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_REQUIREMENT_DESIGN,
      };
      const href = createPath({
        pathname: `/component-project-list?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    };
    if (eventName === "inProgressCmpProjectName" && selectedRow) {
      inProgressCmpProjectNameLogic();
    }
    if (eventName === "waitingCmpProjectName" && selectedRow) {
      waitingCmpProjectNameLogic();
    }
    if (eventName === "inProgressCmpProjectName_newTabIcon" && selectedRow) {
      inProgressCmpProjectNameNewTabIconLogic();
    }
    if (eventName === "waitingCmpProjectName_newTabIcon" && selectedRow) {
      waitingCmpProjectNameNewTabIconLogic();
    }
  };

  const onCellEditedEdhProtocolSpreadsheet = (event: CustomEvent) => {
    if (!edhProtocolSpreadsheetState) return;
    const value: string = event.detail.cell.value ?? "";
    const eventName = event.detail.name;
    const isChanged = event.detail.cell.row.isChanged;

    const edhPrtcl: SpreadsheetEdhPrtcl = event.detail.value.edhPrtcl;
    const edhPrtclAttribute: GetEdhPrtclAttribute =
      event.detail.value.edhPrtclAttribute;

    const copyEdhPrtclList = JSON.parse(
      JSON.stringify(edhProtocolSpreadsheetState.edhPrtcl),
    ) as SpreadsheetEdhPrtcl[];

    const targetEdhPrtclAttribute =
      edhPrtcl.edhPrtclInfo.edhPrtclAttributeList.find(
        (e: GetEdhPrtclAttribute) =>
          e.edhPrtclAttributenum === edhPrtclAttribute.edhPrtclAttributenum,
      );
    if (!edhPrtclAttribute || !edhPrtcl || !targetEdhPrtclAttribute) return;

    const copyEditCells: EditCell = JSON.parse(
      JSON.stringify(edhProtocolSpreadsheetState.editCell),
    );

    if (eventName === "stgDesignValue" || eventName === "prdDesignValue") {
      const isPrd = eventName === "prdDesignValue";

      edhPrtcl[isPrd ? "isChangedPrd" : "isChangedStg"] = isChanged;
      targetEdhPrtclAttribute[isPrd ? "prdDesignValue" : "stgDesignValue"] =
        value;

      copyEdhPrtclList.map((copyEdhPrtcl) => {
        copyEdhPrtcl.edhPrtclInfo.edhPrtclAttributeList.map(
          (copyEdhPrtclAttribute) => {
            // Same condition as the on_edit condition of the template command
            const isGodCondition =
              copyEdhPrtcl.edhPrtclInfo.designStatus ===
                TcktStsTypeCpntDsgn.DESIGN_APPROVED &&
              copyEdhPrtcl.edhPrtclInfo.inProgressCmpProjectReqType ===
                InDefCommonFlagType.NO &&
              copyEdhPrtcl.edhPrtclInfo.godFlg === InDefCommonFlagType.YES;

            const isCommonCondition =
              copyEdhPrtcl.isEditableAttr &&
              params.godFlg === InDefCommonFlagType.NO &&
              copyEdhPrtcl.edhPrtclInfo.designStatus ===
                TcktStsTypeCpntDsgn.DESIGN &&
              copyEdhPrtcl.edhPrtclInfo.inProgressCmpProjectReqType ===
                InDefCommonFlagType.NO &&
              copyEdhPrtcl.edhPrtclInfo.godFlg === InDefCommonFlagType.NO;

            const isEditable =
              params.godFlg === InDefCommonFlagType.YES
                ? isGodCondition
                : isCommonCondition;

            if (
              copyEdhPrtclAttribute.key === targetEdhPrtclAttribute.key &&
              isEditable
            ) {
              const key =
                (isPrd ? "prdDesignValue" : "stgDesignValue") +
                "-" +
                copyEdhPrtcl["rowIdx"] +
                "-" +
                copyEdhPrtclAttribute["edhPrtclAttributenum"];
              copyEdhPrtclAttribute[
                isPrd ? "prdDesignValue" : "stgDesignValue"
              ] = value;
              copyEditCells[key].isDirty = isChanged;
              copyEditCells[key].currentValue = value;
            }
          },
        );
      });
    }

    const prevState = edhProtocolSpreadsheetState;
    copyEdhPrtclList[edhPrtcl.rowIdx] = edhPrtcl;
    setEdhProtocolSpreadsheetState({
      ...prevState,
      edhPrtcl: copyEdhPrtclList,
      editCell: copyEditCells,
    });
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!edhProtocolSpreadsheetState) return;
    const colName = event.detail.name as keyof GetEdhPrtclAttribute;
    const newEdhProtocolList = _.cloneDeep(
      edhProtocolSpreadsheetState?.edhPrtcl,
    ) as SpreadsheetEdhPrtcl[];
    const copyEditCells: EditCell = edhProtocolSpreadsheetState.editCell;

    const dataNum = newEdhProtocolList.map(
      (item, index) => item.edhPrtclInfo.edhPrtclAttributeList.length || 1,
    );

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const getIdxs = (rowIdx: number) => {
        let parentIdx = 0;
        let rowSums = 0;
        for (parentIdx; parentIdx < dataNum.length; parentIdx++) {
          if (rowSums + dataNum[parentIdx] > rowIdx) break;
          rowSums += dataNum[parentIdx];
        }
        return { parentIdx, rowSums };
      };
      const value: string = item.value;
      const rowIdx: number = item.row.index - 1;
      const isChanged = item.row.isChanged;
      let rowSums = 0;
      // there is used union cells in spreadsheet, so need to calculate parent index
      const parentIdx = rowIdx > 0 ? getIdxs(rowIdx).parentIdx : 0;

      const edhProtocolAttribute =
        newEdhProtocolList[parentIdx].edhPrtclInfo.edhPrtclAttributeList[
          rowIdx - getIdxs(rowIdx).rowSums
        ];
      if (colName === "stgDesignValue") {
        const key =
          "stgDesignValue" +
          "-" +
          newEdhProtocolList[parentIdx].rowIdx +
          "-" +
          edhProtocolAttribute.edhPrtclAttributenum;

        copyEditCells[key].isDirty = isChanged;
        newEdhProtocolList[parentIdx].isChangedStg = isChanged;
        copyEditCells[key].currentValue = value;
      } else if (colName === "prdDesignValue") {
        const key =
          "prdDesignValue" +
          "-" +
          newEdhProtocolList[parentIdx].rowIdx +
          "-" +
          edhProtocolAttribute.edhPrtclAttributenum;
        copyEditCells[key].isDirty = isChanged;
        newEdhProtocolList[parentIdx].isChangedPrd = isChanged;
        copyEditCells[key].currentValue = value;
      }
      edhProtocolAttribute[colName] = value;
    });

    setEdhProtocolSpreadsheetState({
      ...edhProtocolSpreadsheetState,
      edhPrtcl: newEdhProtocolList,
      editCell: copyEditCells,
    });
  };

  const onClickReloadButton = () => {
    const getComponent = () => {
      sendGetComponentRequest({
        rootComponentId: params.rootCpntId,
        componentId: params.cpntId,
        dipByRgn: params.dipByRgn,
        godFlg: params.godFlg,
      });
    };

    if (isChangedEdhProtocolAttribute) {
      createDiscardDialog(getComponent);
    } else {
      getComponent();
    }
  };

  const onClickReviewButton = () => {
    const goReview = () => {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        mode: "review",
      };
      transition(`/edh-protocol-attribute?${stringify(navigationParameters)}`);
    };

    if (isChangedEdhProtocolAttribute) {
      createDiscardDialog(goReview);
    } else {
      goReview();
    }
  };

  const onClickSelectReviewTargetButton = () => {
    const goReviewRequest = () => {
      const reviewRequestParams = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        mode: EDH_PROTOCOL_ATTRIBUTE_PAGE_MODE.REQUEST_APPROVAL,
      };
      transition(`/edh-protocol-attribute?${stringify(reviewRequestParams)}`);
    };

    if (isChangedEdhProtocolAttribute) {
      createDiscardDialog(goReviewRequest);
    } else {
      goReviewRequest();
    }
  };

  const onClickSaveButton = () => {
    resetUserError();
    if (!edhProtocolSpreadsheetState) return;

    // clear error status on column
    let copyEditCell: EditCell = JSON.parse(
      JSON.stringify(edhProtocolSpreadsheetState.editCell),
    );
    for (const prop in copyEditCell) {
      copyEditCell[prop].isError = false;
    }
    setEdhProtocolSpreadsheetState({
      ...edhProtocolSpreadsheetState,
      editCell: copyEditCell,
    });

    const deduplicatedCpntAttrList = changedEdhPrtclList.filter(
      (e, index, self) =>
        self.findIndex(
          (f) => f.envTypeCd === e.envTypeCd && f.cpntAttrNum === e.cpntAttrNum,
        ) === index,
    );

    const sendData: UpdateComponentAttributesRequest = {
      cpntId: searchResult.cpntId,
      dipByRgn: searchResult.dipByRgn,
      godFlg: params.godFlg,
      cpntAttrList: deduplicatedCpntAttrList,
    };
    sendUpdateComponentAttributesRequest(sendData);
  };

  /**
   * Footer
   */
  const FooterContent: React.FC = () => {
    return (
      <FrVrfFooter>
        <FrVrfButton
          content="Back"
          slot="left"
          theme="secondary"
          size="large"
          onClicked={() => {
            if (isChangedEdhProtocolAttribute) {
              createDiscardDialog(() => {
                navigate(-1);
              });
            } else {
              navigate(-1);
            }
          }}
        />
      </FrVrfFooter>
    );
  };

  return (
    <>
      {/* GOD */}
      {params.godFlg !== InDefCommonFlagType.YES && (
        <>
          <GodContent />
          <br />
        </>
      )}
      {/* Root Component */}
      <FrVrfAccordion summary="Root component">
        <FrVrfPanel>
          <FrVrfFormPanel columns={2}>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Root Component ID"
                firstValue={searchResult.rootCpntId}
              />
              <FrVrfSingleLineDisplayField
                label="Root Component Name"
                firstValue={searchResult.rootCpntName}
              />
              <FrVrfSingleLineDisplayField
                label="DIP By Region"
                firstValue={searchResult.dipByRgn}
              />
              <FrVrfSingleLineDisplayField
                label="Owner Type"
                firstValue={searchResult.ownTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="System Name"
                firstValue={searchResult.systemName}
              />
              <FrVrfSingleLineDisplayField
                label="System ID"
                firstValue={searchResult.systemId}
              />
              <FrVrfSingleLineDisplayField
                label="Sub System ID"
                firstValue={searchResult.subSystemId}
              />
              <FrVrfCheckboxField
                labelContent="Not System Usage"
                checked={isChecked(searchResult.notSysUsg)}
                disabled
              />
            </FrVrfFormPanel>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Component Allocation Type"
                firstValue={searchResult.alctnTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="Target Cloud Services"
                firstValue={searchResult.clodSrvcTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="Target Cloud Region"
                firstValue={searchResult.rgnTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="Target Cloud Account"
                firstValue={searchResult.acntName}
              />
              <FrVrfPanel panelTitle="Pic Address">
                <FrVrfSpreadsheet
                  data={picAddressSpreadsheetInitialData}
                  onInitialized={initPicAddressSpreadsheetState}
                  templateUrl={TEMPLATE_FILE_URL.PIC_ADDRESS}
                  maxHeight={320}
                />
              </FrVrfPanel>
            </FrVrfFormPanel>
          </FrVrfFormPanel>
          <FrVrfButtonPanel>
            <FrVrfButton
              content="Edit"
              size="medium"
              theme="secondary"
              onClicked={onClickRootComponentEditButton}
              disabled={searchResult.rootCpntDelFlg === InDefCommonFlagType.YES}
              authActId="component_detail.button.root_component_edit"
            />
          </FrVrfButtonPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <br />
      {/* Component */}
      <FrVrfAccordion summary="Component">
        <FrVrfPanel>
          <FrVrfFormPanel columns={2}>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Component ID"
                firstValue={searchResult.cpntId}
              />
              <FrVrfSingleLineDisplayField
                label="Component Name"
                firstValue={searchResult.cpntName}
              />
              <FrVrfSingleLineDisplayField
                label="Component Name (Waiting to be changed)"
                firstValue={searchResult.cpntNameWtngChng}
              />
              <FrVrfMultipleLineInputField
                tabindex="1"
                labelContent="Description"
                resize="both"
                content={searchResult.cpntDesc}
                disabled
              />
              <FrVrfCheckboxField
                labelContent="GPG Encryption"
                checked={isChecked(searchResult.gpgEncryptnFlg)}
                disabled
              />
            </FrVrfFormPanel>
            <FrVrfFormPanel>
              <FrVrfCheckboxField
                labelContent="Use Assumed Role"
                checked={isChecked(searchResult.useAssmdRoleFlg)}
                disabled
              />
              <FrVrfCheckboxField
                labelContent="Use EDH Converter"
                checked={isChecked(searchResult.useEdhCnvtrFlg)}
                disabled
              />
              <FrVrfCheckboxField
                labelContent="HUB Flag"
                checked={isChecked(searchResult.hubFlg)}
                disabled
              />
              <FrVrfCheckboxesField
                disabled
                labelContent="EDH Protocol"
                options={dataList.dynamicEdhProtocolList}
                checkList={searchResult.edhPrtclList.map(
                  (e) => e.inOutTypeCd + "/" + e.edhPrtclTypeCd,
                )}
              />
              <FrVrfCheckboxesField
                disabled
                labelContent="IF Start Method"
                options={dataList.startMethod}
                checkList={searchResult.ifStrtMthdList.map(
                  (e) => e.ifStrtMthdTypeCd,
                )}
              />
              <FrVrfCheckboxesField
                disabled
                labelContent="Inbound Notificator"
                options={dataList.inboundNotificator}
                checkList={searchResult.edhNtfctnTypeList.map((e) =>
                  IoType.INBOUND === e.inOutTypeCd ? e.edhNtfctnTypeCd : "",
                )}
              />
              <FrVrfCheckboxesField
                disabled
                labelContent="Outbound Notificator"
                options={dataList.outboundNotificator}
                checkList={searchResult.edhNtfctnTypeList.map((e) =>
                  IoType.OUTBOUND === e.inOutTypeCd ? e.edhNtfctnTypeCd : "",
                )}
              />
              <FrVrfCheckboxField
                disabled
                labelContent="EDI Usage"
                checked={isChecked(searchResult.ediUsgFlg)}
              />
              {searchResult.ediUsgFlg === InDefCommonFlagType.YES && (
                <FrVrfSingleLinkDisplayField
                  labelContent=""
                  content="Partner List"
                  onClicked={onClickedPartnerListLink}
                />
              )}
            </FrVrfFormPanel>
          </FrVrfFormPanel>
          <FrVrfButtonPanel>
            <FrVrfButton
              content="Deploy Component Name"
              size="medium"
              theme="primary"
              onClicked={onClickDeployComponentNameButton}
              disabled={
                searchResult.cpntDelFlg === InDefCommonFlagType.YES ||
                searchResult.cpntNameWtngChng === null
              }
              authActId="component_detail.button.deploy_component_name"
            />
            <FrVrfButton
              content="Edit"
              size="medium"
              theme="secondary"
              onClicked={onClickComponentEditButton}
              disabled={searchResult.cpntDelFlg === InDefCommonFlagType.YES}
              authActId="component_detail.button.component_edit"
            />
          </FrVrfButtonPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <br />
      {/* EDH Protocol Attribute */}
      <FrVrfAccordion summary="EDH Protocol Attribute">
        <FrVrfPanel>
          <FrVrfSpreadsheet
            data={edhProtocolSpreadsheetInitialData}
            templateUrl={TEMPLATE_FILE_URL.EDH_PROTOCOL_ATTRIBUTE}
            onInitialized={initEdhProtocolSpreadsheetState}
            onCellEdited={onCellEditedEdhProtocolSpreadsheet}
            onCellClicked={onClickEdhProtocolSpreadsheet}
            onCellBulkEdited={onCellBulkEdited}
            maxHeight={740}
          />
          <FrVrfButtonPanel>
            <FrVrfButton
              content="Reload"
              theme="secondary"
              size="large"
              onClicked={onClickReloadButton}
              authActId="component_detail.button.reload"
            />
            <FrVrfButton
              content="Review"
              theme="secondary"
              size="large"
              onClicked={onClickReviewButton}
              authActId="component_detail.button.review"
              visible={!isGodMode}
            />
            <FrVrfButton
              content="Select Review Target"
              theme="secondary"
              size="large"
              onClicked={onClickSelectReviewTargetButton}
              authActId="component_detail.button.select_review_target"
              visible={!isGodMode}
            />
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              onClicked={onClickSaveButton}
              disabled={!isChangedEdhProtocolAttribute}
              authActId="component_detail.button.save"
            />
          </FrVrfButtonPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      {/* Footer */}
      <FooterContent />
      {/* Root Component Edit Dialog */}
      {rootComponentEditDialogState.isShow && (
        <RootComponentEditDialogContent />
      )}
      {/* Component Edit Dialog */}
      {componentEditDialogState.isShow && <ComponentEditDialogContent />}
    </>
  );
};
